// Round 108, C4 — how the client learns it has been rate-limited, in a form
// that cannot be silently swallowed.
//
// The backend (src/middleware/rateLimit.ts) answers a limited request with
//   429 { success:false, code:'RATE_LIMITED', retryAfterSeconds, error } + Retry-After
// Many call sites in this app deliberately swallow errors from fire-and-forget
// requests (guiding questions, image slots, background saves), and others turn
// them into per-screen messages. Neither is enough on its own: a learner whose
// background request was throttled would see nothing at all. So apiProxy
// raises ONE window event for every RATE_LIMITED response and a single
// app-level banner (components/errors/RateLimitNotice.tsx) shows the message,
// whatever screen the learner is on and whoever swallowed the error.
//
// Deliberately imports nothing from apiProxy.ts (apiProxy imports THIS file,
// and apiProxy.ts uses import.meta, which ts-jest cannot parse) — same split
// as pages/admin/adminErrors.ts.

export const RATE_LIMITED_CODE = 'RATE_LIMITED';
export const RATE_LIMITED_EVENT = 'emi:rate-limited';

export interface RateLimitedDetail {
  retryAfterSeconds: number;
}

const DEFAULT_RETRY_SECONDS = 10;
const MAX_RETRY_SECONDS = 300;

/** Seconds to wait, from the JSON body first, then the Retry-After header; clamped to 1..300. */
export function parseRetryAfterSeconds(body: unknown, retryAfterHeader: string | null | undefined): number {
  const fromBody = (body as { retryAfterSeconds?: unknown } | null | undefined)?.retryAfterSeconds;
  const candidates = [fromBody, retryAfterHeader != null ? Number(retryAfterHeader) : undefined];
  for (const c of candidates) {
    const n = typeof c === 'number' ? c : Number(c);
    if (Number.isFinite(n) && n > 0) return Math.min(MAX_RETRY_SECONDS, Math.max(1, Math.ceil(n)));
  }
  return DEFAULT_RETRY_SECONDS;
}

/** True only for the limiter's own 429 — NOT the per-learner daily AI quota (also a 429, different meaning). */
export function isRateLimitedBody(status: number, body: unknown): boolean {
  return status === 429 && (body as { code?: unknown } | null | undefined)?.code === RATE_LIMITED_CODE;
}

export function notifyRateLimited(retryAfterSeconds: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<RateLimitedDetail>(RATE_LIMITED_EVENT, { detail: { retryAfterSeconds } }));
}

/**
 * For responses a caller will stream/consume itself (audio): inspects a CLONE
 * so the caller's body is untouched, and raises the event if it is the
 * limiter's 429. Never throws.
 */
export async function notifyIfRateLimited(res: Response): Promise<boolean> {
  if (res.status !== 429) return false;
  try {
    const body = await res.clone().json();
    if (!isRateLimitedBody(res.status, body)) return false;
    notifyRateLimited(parseRetryAfterSeconds(body, res.headers.get('Retry-After')));
    return true;
  } catch {
    return false;
  }
}
